import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';

import { CurrentUser } from 'api/currentUser';
import { FilestackImageData } from 'api/types/shared.types';
import Box from 'components/Box';
import { getUserType, trackEvent } from 'utils/analytics';
import { configString, configValue } from 'utils/config';
import { imageSizeUrl } from 'utils/filestack';
import { getJson } from 'utils/http';

const importFilestack = () =>
  import(
    /* webpackChunkName: "filestack" */
    'filestack-js'
  );

type OwnProps = {
  name: string;
  onChange: (arg0: FilestackImageData | null | undefined) => void;
  aspectRatio?: number;
  disableCrop?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (arg0: Record<string, any>) => ReactNode;
  getDimensions?: boolean;
  label?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraAnalyticsData?: Record<string, any>;
};
type Props = OwnProps & {
  currentUser: CurrentUser;
};

const getMockImage = () => ({
  filename: 'image.jpg',
  handle: configString('filestack', 'handleOverride'),
  mimetype: 'image/jpeg',
  size: 409609,
  source: 'local_file_system',
});

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const FilestackPicker = ({
  name,
  onChange,
  aspectRatio,
  disableCrop,
  children,
  getDimensions,
  label,
  currentUser,
  extraAnalyticsData,
  ...props
}: Props) => {
  const ref = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [filestackClient, setFilestackClient] = useState(null);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  // filestack is supposed to accept a node as container but it's not working
  const id = `fs-picker-${name.replace(/\./g, '_')}`;
  const inputId = `${id}-input`;

  const onMouseEnter = () => {
    setDragActive(true);
  };

  const onMouseLeave = () => {
    setDragActive(false);
  };

  const setInputId = useCallback(() => {
    // accessibility hack
    const el = ref.current;

    if (el) {
      const interval = window.setInterval(() => {
        const inputEl = el.querySelector('input');

        if (inputEl) {
          inputEl.id = inputId;
          inputEl.addEventListener('focus', onMouseEnter);
          inputEl.addEventListener('blur', onMouseLeave);
          window.clearInterval(interval);
        }
      }, 200);
    }
  }, [inputId]);
  const maybeTrackUpload = useCallback(
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (file: Record<string, any>) => {
      if (extraAnalyticsData && extraAnalyticsData.skipTracking) {
        return;
      }

      let fileExtension;

      if (file && file.filename) {
        fileExtension = file.filename.split('.').pop();
      }

      trackEvent('Success: Uploaded Image', {
        user_type: getUserType(currentUser),
        file_extension: fileExtension,
        ...extraAnalyticsData,
      });
    },
    [currentUser, extraAnalyticsData]
  );
  const onSuccess = useCallback(
    (files) => {
      setError(null);
      setLoading(false);
      const file = files[0];
      if (!file) return;
      const { filename, handle, mimetype, size, source } = file;
      const newImage = {
        filename,
        handle,
        mimetype,
        size,
        source,
      };
      maybeTrackUpload(file);

      if (file && getDimensions) {
        getJson(
          imageSizeUrl({
            handle: file.handle,
          })
        ).then(({ width, height }) => {
          onChange({ ...newImage, width, height });
        });
      } else {
        onChange(newImage);
      }
    },
    [getDimensions, onChange, maybeTrackUpload]
  );

  const onError = (err) => {
    if (err && err.length) {
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      setError(getText('There was an error uploading your image.'));
    }
  };

  const getBaseOptions = useCallback(
    () => ({
      accept: 'image/*',
      fromSources: ['local_file_system'],
      uploadInBackground: false,
      transformations: {
        crop: aspectRatio
          ? {
              aspectRatio,
              force: true,
            }
          : !disableCrop,
      },
    }),
    [aspectRatio, disableCrop]
  );

  const fallbackClickHandler = () => {
    if (configValue('filestack', 'mock')) {
      const mockImage = getMockImage();
      maybeTrackUpload(mockImage);
      onChange(mockImage);
    }
  };

  const onUploadDone = (res) => {
    onSuccess(res.filesUploaded);
  };

  const onClick = () => {
    if (configValue('filestack', 'mock')) {
      onChange(getMockImage());
    } else {
      const options = {
        ...getBaseOptions(),
        onUploadDone,
        onUploadStarted: () => setLoading(true),
      };
      importFilestack().then((m) => {
        const client = m.init(configValue('filestack', 'apiKey'));
        client.picker(options).open();
        setInputId();
      });
    }
  };

  useEffect(() => {
    if (!configValue('filestack', 'mock')) {
      const options = {
        ...getBaseOptions(),
        displayMode: 'dropPane',
        container: `#${id}`,
        dropPane: {
          overlay: false,
          onSuccess,
          onError,
          onDragEnter: () => setDragActive(true),
          onDragLeave: () => setDragActive(false),
          onDrop: () => setDragActive(false),
          onProgress: () => setLoading(true),
        },
      };

      if (!filestackClient) {
        importFilestack().then((m) => {
          const client = m.init(configValue('filestack', 'apiKey'));

          if (document.getElementById(id)) {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ displayMode: string; container... Remove this comment to see the full error message
            client.picker(options).open();
            setFilestackClient(client);
            setInputId();
          }
        });
      }
    }
  }, [disableCrop, filestackClient, getBaseOptions, id, onSuccess, setInputId]);
  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box position="relative" {...props}>
      {children({
        onClick,
        dragActive,
        error,
        loading,
      })}
      <div ref={ref}>
        <Box component="label" display="none" htmlFor={inputId}>
          {label}
        </Box>
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          overflow="hidden"
          opacity={0}
          id={id}
          onClick={fallbackClickHandler}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </div>
    </Box>
  );
};

const mapStateToProps = ({ currentUser }) => ({
  currentUser,
});

// @ts-expect-error ts-migrate(2558) FIXME: Expected 5 type arguments, but got 6.
export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  {}
)(FilestackPicker);
